import produce from 'immer';
import { gql } from '@soundxyz/gql-string';
import {
  ERROR_TYPE,
  FEATURES,
  MISCELLANEOUS_ERROR_ACTIONS,
} from '@soundxyz/utils/constants/logging';
import type { NftCursorConnectionSort } from '@graphql/reactQuery';
import { AudienceHookDocument, invalidateOperations, useInfiniteQuery } from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { useSubscribeReleaseCollector } from '@hooks/useSubscribeRelease';

/*#__PURE__*/ gql(/* GraphQL */ `
  query AudienceHook($releaseId: UUID!, $pagination: NftCursorConnectionArgs!) {
    release(id: $releaseId) {
      id
      nftsPaginated(pagination: $pagination) {
        edges {
          node {
            id
            serialNumber
            owner {
              id
              publicAddress
            }
            ...NftCommentType
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`);

export function useAudience({
  releaseId,
  paginationCount,
  sort,
}: {
  releaseId: string;
  paginationCount: number;
  sort: NftCursorConnectionSort;
}) {
  const logError = useLogError();
  const {
    orderedList: nfts,
    hasNextPage = false,
    loadMoreNextPage: fetchMore,
    setInfiniteQueryData,
    isLoadingNewPage: loading,
  } = useInfiniteQuery(AudienceHookDocument, {
    getNextPageParam({ data }) {
      return (
        data.release?.nftsPaginated.pageInfo.hasNextPage && {
          after: data.release.nftsPaginated.pageInfo.endCursor,
        }
      );
    },
    filterQueryKey: {
      releaseId,
      sort,
    },
    variables({ pageParam }) {
      return {
        releaseId,
        pagination: {
          sort,
          first: paginationCount,
          after: pageParam?.after,
        },
      };
    },
    list({ release }) {
      return release?.nftsPaginated.edges.map(edge => edge.node);
    },
    uniq({ id }) {
      return id;
    },
    order: [
      [v => v.serialNumber, v => v.id],
      ['asc', 'asc'],
    ],
    onError(error) {
      logError({
        action: MISCELLANEOUS_ERROR_ACTIONS.AUDIENCE_FETCH_ERROR,
        error,
        errorType: ERROR_TYPE.FETCH_GQL_ERROR,
        isArtistOnlyPage: false,
        level: 'error',
        message: 'Error calling AudienceHook',
        feature: FEATURES.AUDIENCE,
        indexedTags: {
          releaseId,
        },
      });
    },
    staleTime: 0,
  });

  useSubscribeReleaseCollector({
    releaseId,
    onData({
      data: {
        releaseCollectorUpdates: { collector, userPublicAddress },
      },
    }) {
      setInfiniteQueryData(previousData => {
        if (!previousData) return previousData;

        // If a collector was updated or is new collector, just revalidate the paginated data
        if (collector) {
          invalidateOperations({
            operations: [AudienceHookDocument],
          });

          return previousData;
        }

        // This is the path if a user stopped being a collector, we need to cleanup from the list

        return produce(previousData, draft => {
          for (const page of draft.pages) {
            if (!page.data.release) continue;

            const filteredEdges = page.data.release.nftsPaginated.edges.filter(
              edge => edge.node.owner.publicAddress !== userPublicAddress,
            );

            // only update array reference if there is a change
            if (filteredEdges.length !== page.data.release.nftsPaginated.edges.length) {
              page.data.release.nftsPaginated.edges = filteredEdges;
            }
          }
        });
      });
    },
    enabled: true,
  });

  return {
    nfts,
    loading,
    hasNextPage,
    fetchMore,
  };
}
